/**
 * Community archive filtering — replaces the old site's ?year=&month= page loads.
 *
 * The listing renders every post, so filtering is a class toggle rather than a request:
 * years collapse and expand, a month narrows the cards to that month, "View All"
 * restores them, and the URL keeps ?year=&month= so a filtered view is still a link
 * someone can send. On the post page there are no cards, so the same month links fall
 * back to their href and land on the filtered listing.
 *
 * Below 768px the archive is an off-canvas panel opened by the "Filters" button.
 */
(() => {
  const MOBILE = '(max-width: 767px)';

  const init = (root) => {
    const cards = Array.from(root.querySelectorAll('[data-rj-comm-card]'));
    const panel = root.querySelector('[data-rj-comm-panel]');
    const overlay = root.parentElement?.querySelector('[data-rj-comm-overlay]');
    const empty = root.querySelector('[data-rj-comm-empty]');
    const monthLinks = Array.from(root.querySelectorAll('[data-rj-comm-month]'));
    const yearHeads = Array.from(root.querySelectorAll('[data-rj-comm-year]'));

    const closePanel = () => {
      panel?.classList.remove('is-open');
      overlay?.setAttribute('hidden', '');
      document.body.style.removeProperty('overflow');
    };

    const openPanel = () => {
      panel?.classList.add('is-open');
      overlay?.removeAttribute('hidden');
      document.body.style.overflow = 'hidden';
    };

    const expandYear = (year) => {
      yearHeads.forEach((head) => {
        const open = head.dataset.rjCommYear === year;
        head.setAttribute('aria-expanded', String(open));
        const list = head.nextElementSibling;
        if (list) list.toggleAttribute('hidden', !open);
      });
    };

    /** Shows only the posts of that year/month; no month means everything. */
    const apply = (year, month) => {
      let shown = 0;
      cards.forEach((card) => {
        const match = !month || (card.dataset.year === year && card.dataset.month === month);
        card.toggleAttribute('hidden', !match);
        if (match) shown += 1;
      });

      monthLinks.forEach((link) => {
        link.classList.toggle(
          'is-active',
          Boolean(month) && link.dataset.rjCommMonth === month && link.dataset.rjCommMonthYear === year
        );
      });

      if (empty) empty.toggleAttribute('hidden', shown > 0);
      if (month) expandYear(year);
    };

    const sync = (year, month, push) => {
      const url = new URL(window.location.href);
      if (month) {
        url.searchParams.set('year', year);
        url.searchParams.set('month', month);
      } else {
        url.searchParams.delete('year');
        url.searchParams.delete('month');
      }
      if (push) window.history.pushState({ year, month }, '', url);
    };

    const fromUrl = () => {
      const params = new URLSearchParams(window.location.search);
      return { year: params.get('year'), month: params.get('month') };
    };

    yearHeads.forEach((head) => {
      head.addEventListener('click', () => {
        const isOpen = head.getAttribute('aria-expanded') === 'true';
        if (isOpen) {
          head.setAttribute('aria-expanded', 'false');
          head.nextElementSibling?.setAttribute('hidden', '');
        } else {
          expandYear(head.dataset.rjCommYear);
        }
      });
    });

    monthLinks.forEach((link) => {
      link.addEventListener('click', (event) => {
        if (!cards.length) return; // post page: let the link navigate to the listing
        event.preventDefault();
        const { rjCommMonth: month, rjCommMonthYear: year } = link.dataset;
        apply(year, month);
        sync(year, month, true);
        if (window.matchMedia(MOBILE).matches) closePanel();
      });
    });

    root.querySelector('[data-rj-comm-all]')?.addEventListener('click', (event) => {
      if (!cards.length) return;
      event.preventDefault();
      apply(null, null);
      sync(null, null, true);
      if (window.matchMedia(MOBILE).matches) closePanel();
    });

    root.querySelector('[data-rj-comm-open]')?.addEventListener('click', openPanel);
    root.querySelector('[data-rj-comm-close]')?.addEventListener('click', closePanel);
    overlay?.addEventListener('click', closePanel);
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closePanel();
    });

    window.addEventListener('popstate', () => {
      const { year, month } = fromUrl();
      apply(year, month);
    });

    const { year, month } = fromUrl();
    if (month) apply(year, month);
  };

  const boot = () => document.querySelectorAll('[data-rj-comm-root]').forEach(init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
